import React from 'react';
import {
  Page,
  Text,
  Image,
  Svg,
  View,
  Document,
  StyleSheet,
  Path,
} from '@react-pdf/renderer';
import { needToUseSsn } from '../../../../../functions/needToUseSsn';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignContent: 'space-between',
    backgroundColor: '#FFF',
    padding: '12px 0 0 48px',
    alignItems: 'stretch',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    alignItems: 'flex-start',
    // height: '100%',
    backgroundColor: '#FFF',
  },
  contentText: {
    margin: '0',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexGrow: 1,
  },
  sectionImage: {
    margin: 0,
    padding: 0,
    flexGrow: 1,
  },
  title: {
    color: '#ACB3B8',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '100%',
    marginBottom: '3px',
  },
  text: {
    color: '#011627',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '100%',
    marginBottom: '9px',
  },
  image: {
    objectFit: 'contain',
    maxWidth: '90%',
    maxHeight: '320px',
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    height: '64px',
  },
  footerText: {
    color: '#ACB3B8',
    fontWeight: 500,
    fontSize: '10px',
  },
  imageCapture: {
    width: '147px',
    position: 'relative',
    top: '-30px',
    left: '230px',
  },
  imageCaptureTitle: {
    color: '#011627',
    fontWeight: 400,
    fontSize: '8px',
    lineHeight: '100%',
  },
  imageCaptureDate: {
    color: '#011627',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '100%',
  },
});

type InputProps = {
  snn?: string;
  driverName: string;
  employeeId: string;
  ticketId: string;
  dateOnboarded: string;
  screenshotCreatedAt: string;
  approvedAt: string;
  firstImage?: string;
  secondImage?: string;
};

export const DOTMedicalCardPdf = ({
  snn,
  driverName,
  ticketId,
  dateOnboarded,
  screenshotCreatedAt,
  approvedAt,
  firstImage,
  secondImage,
}: InputProps) => {
  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.contentText}>
          <View style={styles.row}>
            {snn && (
              <View style={styles.row}>
                <Text style={styles.title}>
                  {needToUseSsn() ? 'Driver’s SSN:' : 'Alpha Code:'}
                </Text>
                <Text style={styles.text}>{snn}</Text>
              </View>
            )}
            {driverName && (
              <View style={styles.row}>
                <Text style={styles.title}>Driver:</Text>
                <Text style={styles.text}>{driverName}</Text>
              </View>
            )}
          </View>
          <View style={styles.row}>
            {ticketId && (
              <View style={styles.row}>
                <Text style={styles.title}>HaulSafe Ticket Number:</Text>
                <Text style={styles.text}>{ticketId}</Text>
              </View>
            )}
            {dateOnboarded && (
              <View style={styles.row}>
                <Text style={styles.title}>Date Updated:</Text>
                <Text style={styles.text}>{dateOnboarded}</Text>
              </View>
            )}
          </View>
        </View>
        {firstImage && (
          <View style={styles.sectionImage}>
            <Image style={styles.image} src={firstImage} />
            {screenshotCreatedAt && (
              <View style={styles.imageCapture}>
                <Text style={styles.imageCaptureTitle}>Captured at</Text>
                <Text style={styles.imageCaptureDate}>
                  {screenshotCreatedAt}
                </Text>
              </View>
            )}
            {!screenshotCreatedAt && approvedAt && (
              <View style={styles.imageCapture}>
                <Text style={styles.imageCaptureTitle}>Approved at</Text>
                <Text style={styles.imageCaptureDate}>{approvedAt}</Text>
              </View>
            )}
          </View>
        )}
        <View>
          {secondImage && <Image style={styles.image} src={secondImage} />}
        </View>
        <View style={styles.footer}>
          <Text style={styles.footerText}>Powered by</Text>
          <Svg style={{ width: 84, height: 28 }} viewBox="0 0 84 28">
            <Path
              fill-rule="evenodd"
              clip-rule="evenodd"
              fill="#011627"
              d="M60.6083 6.5116L54.6511 16.8551H59.0618L65.019 6.5116H71.6352L63.0343 21.4454H60.3684V21.4502H50.2841L50.2841 21.4454H45.3913L53.9922 6.5116H60.6083ZM82.662 6.5116L76.7048 16.8551H78.3834L75.737 21.4502L71.3694 21.4502L71.3694 21.4454H67.4449L76.0459 6.5116H82.662ZM83.945 11.9421V21.4454L81.4537 21.4454L81.4537 21.4502L78.4629 21.4502L81.1093 16.8551H81.1154L83.945 11.9421ZM16.501 6.5116L13.4135 11.8724H15.0992L12.6733 16.0845H10.9876L7.90005 21.4454H1.28403L9.88494 6.5116H16.501ZM38.5546 6.5116L38.5519 6.51636H42.9626L42.9654 6.5116H49.5815L40.9805 21.4454H34.367L39.1114 13.2078L39.1094 13.2067L40.3162 11.1114H35.9055L34.188 14.0933H35.8753L33.4494 18.3055H31.7621L29.9537 21.4454H23.3376L31.9386 6.5116H38.5546ZM27.5278 6.5116L18.9269 21.4454H12.3118L20.9127 6.5116H27.5278ZM5.47424 6.5116L0.000976562 16.0149V6.5116L5.47424 6.5116Z"
            />
          </Svg>
        </View>
      </Page>
    </Document>
  );
};
